import { Database } from "../supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";

export interface Venue {
  id: string;
  name: string;
  postal_code: string;
  country: string;
  country_code: string;
  city: string;
  state: string;
  address: string;
  location: Coordinates | null;
  is_pending?: boolean;
  created_by?: string | undefined;
  created_at?: string | undefined;
}

export type VenueRow = Database["public"]["Tables"]["venues"]["Row"];
export type VenueInsert = Database["public"]["Tables"]["venues"]["Insert"];
export type Coordinates = {
  latitude: number;
  longitude: number;
};

function processVenueData(data: VenueRow): Venue {
  return {
    id: data.id,
    name: data.name || "",
    postal_code: data.postal_code || "",
    country: data.country || "",
    country_code: data.country_code || "",
    address: data.address || "",
    city: data.city || "",
    state: data.state || "",
    location: (data.location as Coordinates) || null,
    is_pending: data.is_pending || false,
    created_by: data.created_by || undefined,
    created_at: data.created_at || undefined,
  };
}

export async function getOneVenue(
  supabase: SupabaseClient<Database>,
  venueId: string
): Promise<Venue> {
  const { data, error } = await supabase
    .from("venues")
    .select("*")
    .eq("id", venueId)
    .maybeSingle();

  if (error) {
    console.error(error);
  } else if (data) {
    return processVenueData(data);
  }
  throw new Error(`Issue getting venues`);
}

export async function getAllVenues(
  supabase: SupabaseClient<Database>
): Promise<Venue[]> {
  const allVenues = [];
  // Defines how many venues to fetch per query, 1000 is the maximum value for Supabase
  const PAGE_SIZE = 1000;
  let cursor = 0;
  let done = false;
  while (!done) {
    const venues = await getVenuesRange(
      supabase,
      cursor,
      cursor + PAGE_SIZE - 1
    );
    allVenues.push(...venues);
    cursor += PAGE_SIZE;
    if (venues.length < PAGE_SIZE) {
      done = true;
    }
  }
  return allVenues;
}

export type AllVenueData = {
  // venues mapped as [venueName, venueId, cityAndState]
  venuesArray: [string, string, string][];
  venuesIdDictionary: {
    [venueId: string]: {
      name: string;
      cityAndState: string;
    };
  };
};

export async function getAllVenuesDataForTourDates(
  supabase: SupabaseClient<Database>
): Promise<AllVenueData> {
  const venues = await getAllVenues(supabase);
  // Create a map of venues using the venue id as key and its name ID as value
  const venuesIdDictionary: {
    [venueId: string]: {
      name: string;
      cityAndState: string;
    };
  } = {};

  // Also create an array of the venues at the backend for improved performance
  const venuesArray: [string, string, string][] = []; // [venueName, id, cityAndState]
  venues.forEach((venue) => {
    venuesIdDictionary[venue["id"]] = {
      name: venue["name"],
      cityAndState: `${venue["city"]}, ${venue["state"]}`,
    };
    venuesArray.push([
      venue["name"],
      venue["id"],
      `${venue["city"]}, ${venue["state"]}`,
    ]);
  });

  return { venuesIdDictionary, venuesArray };
}

async function getVenuesRange(
  supabase: SupabaseClient<Database>,
  start: number,
  end: number
): Promise<Venue[]> {
  const { data, error } = await supabase
    .from("venues")
    .select("*")
    .eq("is_deleted", false)
    .order("name", { ascending: true })
    .range(start, end);

  if (error) {
    console.error(error);
  } else if (data) {
    return data.map((row) => processVenueData(row));
  }
  throw new Error(`Issue getting venues`);
}

export async function upsertVenue(
  supabase: SupabaseClient<Database>,
  venue: VenueInsert
) {
  const { data, count, status, error } = await supabase
    .from("venues")
    .upsert(venue, {
      onConflict: "id",
      ignoreDuplicates: false,
      count: "exact",
    })
    .select("*");

  if (error) {
    console.error(error);
    return { error: error };
  } else if (status === 201) {
    if (count === 1 && data) {
      return { error: null, data: processVenueData(data[0]) };
    } else {
      // Log in Sentry that something happened that caused row count update to be not 1
      return {
        error: `Unexpected row count create/update different than 1 for ${venue.name} creation/update`,
      };
    }
  }

  throw new Error(`Error updating ${venue.name}`);
}

export async function bulkUpsertVenues(
  supabase: SupabaseClient<Database>,
  venues: VenueInsert[]
) {
  const { data, error } = await supabase.from("venues").upsert(venues, {
    onConflict: "name, postal_code",
    ignoreDuplicates: true,
    count: "exact",
  });

  if (error) {
    console.error(error);
    return { error: error };
  }

  return { data };
}

export async function deleteVenue(
  supabase: SupabaseClient<Database>,
  venueId: string
): Promise<void> {
  const { count } = await supabase
    .from("venues")
    .delete({ count: "exact" })
    .eq("id", venueId)
    .throwOnError();

  if (count !== 1) {
    throw new Error(
      `Unexpected row count for ${venueId} deletion. Count was: ${count}`
    );
  }
}

// Used to determine if two venues are close to each other
export function arePointsClose(
  point1: Coordinates,
  point2: Coordinates,
  distanceInMeters = 200
) {
  const toRadians = (degrees: number) => degrees * (Math.PI / 180);

  const earthRadius = 6371000; // Earth's radius in meters
  const dLat = toRadians(point2.latitude - point1.latitude);
  const dLon = toRadians(point2.longitude - point1.longitude);

  const lat1 = toRadians(point1.latitude);
  const lat2 = toRadians(point2.latitude);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distance = earthRadius * c;
  return distance <= distanceInMeters;
}
